/*
  Product gallery (snippets/product-media-gallery.liquid). Phones: the stage
  (section-main-product-stage.css), one view at a time, changed by swipe (decided on lift), the
  arrows, the thumbnails or the arrow keys while focus is inside (a playing video keeps its own
  drags and keys); a fade, instant under reduced motion; the last input wins. Hidden views load
  when shown, their neighbours once the phone is idle or on a touch. At every width since desktop-01; without the stage CSS, a scroll-snap row the buttons and thumbnails scroll.
  Both announce the new view through announce() (global.js), never on load.
*/
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('.gallery__body');
        this.viewer = this.querySelector('.gallery__viewer');
        this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
        this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
        this.counter = this.querySelector('[data-gallery-current]');
        this.label = this.querySelector('[data-gallery-label]');
        if (!this.body || !this.viewer || this.slides.length < 2) return;

        this.current = this.target = 0;
        // The stage CSS's own test: without the scripting media feature, the row stays. Every width
        // since desktop-01 (the name is kept from when the stage was phones only).
        this.phone = matchMedia('screen and (scripting: enabled)');
        // The CDN minifies CSS (170ms arrives as .17s), so read the unit.
        const fade = getComputedStyle(this).getPropertyValue('--dur-fade').trim();
        this.fadeTime = parseFloat(fade) * (fade.endsWith('ms') ? 1 : 1000) || 170;

        this.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('[data-gallery-thumbnail]');
          const step = event.target.closest('[data-gallery-step]');
          if (thumbnail) this.show(this.thumbnails.indexOf(thumbnail));
          if (step) this.step(Number(step.dataset.galleryStep));
        });
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('focusin', () => this.warm(this.current));
        this.body.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.body.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.body.addEventListener('pointercancel', () => (this.swipe = null));
        this.viewer.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.onModeChange = this.onModeChange.bind(this);
        this.phone.addEventListener('change', this.onModeChange);

        const warmFirst = () => this.warm(this.current);
        this.onLoad = () => (window.requestIdleCallback ? requestIdleCallback(warmFirst, { timeout: 3000 }) : setTimeout(warmFirst, 200));
        if (document.readyState === 'complete') this.onLoad();
        else window.addEventListener('load', this.onLoad, { once: true });
      }

      disconnectedCallback() {
        this.phone?.removeEventListener('change', this.onModeChange);
        window.removeEventListener('load', this.onLoad);
        clearTimeout(this.scrollTimer);
        clearTimeout(this.fadeTimer);
      }

      // Phones: start loading the hidden view at `index` and its neighbours.
      warm(index) {
        if (!this.phone.matches) return;
        const count = this.slides.length;
        for (const i of [index - 1, index, index + 1]) {
          this.slides[(i + count) % count].querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
        }
      }

      step(direction) {
        const count = this.slides.length;
        this.show((this.target + direction + count) % count);
      }

      show(index) {
        if (!this.slides[index]) return;
        if (!this.phone.matches) {
          this.viewer.scrollTo({ left: this.slides[index].offsetLeft, behavior: scrollBehavior() });
          return this.setCurrent(index);
        }
        this.target = index;
        this.warm(index);
        // A change under way lands on the newest target.
        if (this.fading || index === this.current) return;
        if (reducedMotion()) return this.setCurrent(index);

        this.fading = true;
        this.viewer.classList.add('gallery__viewer--fading');
        this.fadeTimer = setTimeout(() => {
          this.setCurrent(this.target);
          // Two frames, so the new view is drawn hidden before it fades in.
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              this.viewer.classList.remove('gallery__viewer--fading');
              this.fading = false;
              if (this.target !== this.current) this.show(this.target);
            })
          );
        }, this.fadeTime);
      }

      setCurrent(index) {
        if (index === this.current || !this.slides[index]) return;
        // A focused video poster leaving with its view: focus stays in the gallery.
        const hadFocus = this.slides[this.current].contains(document.activeElement);
        const slide = this.slides[index];
        this.current = this.target = index;
        this.slides.forEach((item) => item.classList.toggle('gallery__slide--current', item === slide));
        this.thumbnails.forEach((thumbnail, i) =>
          i === index ? thumbnail.setAttribute('aria-current', 'true') : thumbnail.removeAttribute('aria-current')
        );
        if (this.counter) this.counter.textContent = index + 1;
        if (this.label) this.label.textContent = slide.dataset.position;
        if (hadFocus && this.phone.matches) this.viewer.focus({ preventScroll: true });
        announce(slide.dataset.announcement);
        pauseAllMedia();
      }

      // A playing video keeps its arrow keys (they seek; its controls' events come from the <video>).
      onKeydown(event) {
        if (!this.phone.matches || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('input, select, textarea, video')) return;
        event.preventDefault();
        this.step(event.key === 'ArrowLeft' ? -1 : 1);
      }

      // A swipe starts anywhere on the stage but a control, a playing video (its timeline seeks) or the
      // thumbnail row (it scrolls).
      onPointerDown(event) {
        this.swipe = null;
        if (!this.phone.matches || !event.isPrimary || event.button) return;
        this.warm(this.current);
        if (!event.target.closest('button, a, video, .gallery__rail')) this.swipe = { x: event.clientX, y: event.clientY };
      }

      onPointerUp(event) {
        if (!this.swipe) return;
        const dx = event.clientX - this.swipe.x;
        const dy = event.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) this.step(dx < 0 ? 1 : -1);
      }

      // Across 750px: drop a fade under way; the row scrolls to the current frame.
      onModeChange() {
        clearTimeout(this.fadeTimer);
        this.fading = false;
        this.viewer.classList.remove('gallery__viewer--fading');
        this.setCurrent(this.target);
        if (!this.phone.matches) this.viewer.scrollTo({ left: this.slides[this.current].offsetLeft });
      }

      // The row: once it stops moving, so a glide past frames announces only where it lands.
      onScroll() {
        if (this.phone.matches) return;
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          const width = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
          if (width > 0) this.setCurrent(Math.round(this.viewer.scrollLeft / width));
        }, 100);
      }
    }
  );
}
